import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import {
  Mail, Upload, Loader2, AlertCircle, AlertTriangle, Check,
  Calendar, User, FileText, Download, Pencil, Trash2, Bell,
  X, ChevronDown, ChevronUp, Info
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MailScansSection = ({ companyId, activeServices, companyName }) => {
  const [scans, setScans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUpload, setShowUpload] = useState(false);
  const [showEdit, setShowEdit] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [sortDir, setSortDir] = useState('desc');
  const [page, setPage] = useState(0);
  const PER_PAGE = 25;

  // Upload form state
  const [uploadFile, setUploadFile] = useState(null);
  const [mailType, setMailType] = useState('');
  const [sender, setSender] = useState('');
  const [receivedDate, setReceivedDate] = useState(new Date().toISOString().split('T')[0]);
  const [adminNote, setAdminNote] = useState('');
  const [urgency, setUrgency] = useState('routine');
  const [notifyNow, setNotifyNow] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadErr, setUploadErr] = useState('');
  const fileRef = useRef(null);

  // Edit form state
  const [editSender, setEditSender] = useState('');
  const [editNote, setEditNote] = useState('');
  const [editUrgency, setEditUrgency] = useState('');
  const [saving, setSaving] = useState(false);
  const [editErr, setEditErr] = useState('');

  // Action states
  const [deletingId, setDeletingId] = useState(null);
  const [downloadingId, setDownloadingId] = useState(null);
  const [notifyingId, setNotifyingId] = useState(null);

  const hasRA = activeServices.includes('registered_agent');
  const hasMF = activeServices.includes('mail_forwarding');
  const hasAny = hasRA || hasMF;

  useEffect(() => {
    if (hasAny) fetchScans();
    else setLoading(false);
  }, [companyId]);

  // Auto-select mail type if only one service active
  useEffect(() => {
    if (hasRA && !hasMF) setMailType('ra_mail_scan');
    else if (hasMF && !hasRA) setMailType('mail_forward_scan');
    else setMailType('');
  }, [hasRA, hasMF]);

  // Auto-toggle notify based on urgency
  useEffect(() => {
    setNotifyNow(urgency === 'urgent');
  }, [urgency]);

  const token = () => localStorage.getItem('auth_token');
  const authH = () => ({ Authorization: `Bearer ${token()}` });

  const fetchScans = async () => {
    try {
      setLoading(true);
      const r = await axios.get(`${API}/companies/${companyId}/mail-scans`, { headers: authH() });
      setScans(r.data.mail_scans || []);
    } catch { setScans([]); }
    finally { setLoading(false); }
  };

  const resetUploadForm = () => {
    setUploadFile(null);
    setSender('');
    setReceivedDate(new Date().toISOString().split('T')[0]);
    setAdminNote('');
    setUrgency('routine');
    setNotifyNow(false);
    setUploadErr('');
    if (hasRA && !hasMF) setMailType('ra_mail_scan');
    else if (hasMF && !hasRA) setMailType('mail_forward_scan');
    else setMailType('');
    if (fileRef.current) fileRef.current.value = '';
  };

  const handleFileSelect = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.type !== 'application/pdf') { setUploadErr('Only PDF files are accepted'); return; }
    if (f.size > 10 * 1024 * 1024) { setUploadErr('File size must be under 10MB'); return; }
    setUploadFile(f);
    setUploadErr('');
  };

  const validateDate = (d) => {
    const dt = new Date(d + 'T00:00:00Z');
    const now = new Date();
    const today = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
    if (dt > today) return 'Date cannot be in the future';
    const thirtyAgo = new Date(today); thirtyAgo.setDate(thirtyAgo.getDate() - 30);
    if (dt < thirtyAgo) return 'Date is more than 30 days ago';
    return null;
  };

  const canSubmit = uploadFile && mailType && sender.trim() && receivedDate && !validateDate(receivedDate);

  const handleUpload = async () => {
    if (!canSubmit) return;
    setUploading(true);
    setUploadErr('');
    try {
      const fd = new FormData();
      fd.append('file', uploadFile);
      fd.append('mail_type', mailType);
      fd.append('mail_sender', sender.trim());
      fd.append('mail_received_date', receivedDate);
      fd.append('mail_urgency', urgency);
      if (adminNote.trim()) fd.append('mail_admin_note', adminNote.trim());
      fd.append('notify_now', notifyNow ? 'true' : 'false');

      const r = await axios.post(`${API}/companies/${companyId}/mail-scans`, fd, {
        headers: { ...authH(), 'Content-Type': 'multipart/form-data' }
      });
      setShowUpload(false);
      resetUploadForm();
      fetchScans();
      // Toast-like notification
      const notified = r.data.mail_notified_at;
      showToast(notified ? 'Mail scan uploaded and client notified by email' : 'Mail scan uploaded');
    } catch (err) {
      setUploadErr(err.response?.data?.detail || 'Upload failed');
    } finally { setUploading(false); }
  };

  const handleEdit = async (docId) => {
    setSaving(true);
    setEditErr('');
    try {
      const body = {};
      if (editSender.trim()) body.mail_sender = editSender.trim();
      if (editNote !== undefined) body.mail_admin_note = editNote;
      if (editUrgency) body.mail_urgency = editUrgency;
      await axios.patch(`${API}/mail-scans/${docId}`, body, { headers: { ...authH(), 'Content-Type': 'application/json' } });
      setShowEdit(null);
      fetchScans();
      showToast('Mail scan updated');
    } catch (err) {
      setEditErr(err.response?.data?.detail || 'Update failed');
    } finally { setSaving(false); }
  };

  const handleDelete = async (docId) => {
    setDeletingId(docId);
    try {
      await axios.delete(`${API}/mail-scans/${docId}`, { headers: authH() });
      setShowDeleteConfirm(null);
      fetchScans();
      showToast('Mail scan deleted');
    } catch (err) {
      alert(err.response?.data?.detail || 'Delete failed');
    } finally { setDeletingId(null); }
  };

  const handleDownload = async (doc) => {
    setDownloadingId(doc.id);
    try {
      const r = await axios.get(`${API}/company-documents/${doc.id}/download`, { headers: authH() });
      const url = r.data.download_url || r.data.presigned_url;
      if (url) window.open(url, '_blank');
    } catch (err) {
      alert(err.response?.data?.detail || 'Download failed');
    } finally { setDownloadingId(null); }
  };

  const handleNotify = async (doc) => {
    setNotifyingId(doc.id);
    try {
      await axios.post(`${API}/mail-scans/${doc.id}/notify`, {}, { headers: authH() });
      fetchScans();
      showToast('Client notified by email');
    } catch (err) {
      alert(err.response?.data?.detail || 'Notification failed');
    } finally { setNotifyingId(null); }
  };

  // Simple toast
  const [toast, setToast] = useState(null);
  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(null), 4000); };

  const openEdit = (doc) => {
    setEditSender(doc.mail_sender || '');
    setEditNote(doc.mail_admin_note || '');
    setEditUrgency(doc.mail_urgency || 'routine');
    setEditErr('');
    setShowEdit(doc);
  };

  const formatDate = (d) => {
    if (!d) return '—';
    try { return new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }); }
    catch { return d; }
  };

  const formatBytes = (b) => {
    if (!b) return '';
    if (b < 1024) return `${b} B`;
    if (b < 1048576) return `${(b / 1024).toFixed(1)} KB`;
    return `${(b / 1048576).toFixed(1)} MB`;
  };

  const sorted = [...scans].sort((a, b) =>
    sortDir === 'desc' ? (b.mail_received_date || '').localeCompare(a.mail_received_date || '')
                       : (a.mail_received_date || '').localeCompare(b.mail_received_date || '')
  );
  const paginated = sorted.slice(page * PER_PAGE, (page + 1) * PER_PAGE);
  const totalPages = Math.ceil(sorted.length / PER_PAGE);

  // No active service → disabled message
  if (!hasAny) {
    return (
      <div className="bg-[#0D1117] border border-gray-700/50 rounded-xl p-12 text-center" data-testid="mail-scans-disabled">
        <AlertCircle className="w-12 h-12 text-gray-600 mx-auto mb-4" />
        <p className="text-gray-400 text-lg">This company does not have an active Registered Agent or Mail Forwarding service.</p>
        <p className="text-gray-500 text-sm mt-2">Upload is disabled.</p>
      </div>
    );
  }

  if (loading) {
    return <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 text-[#00E87A] animate-spin" /></div>;
  }

  return (
    <div data-testid="mail-scans-tab-content">
      {/* Toast */}
      {toast && (
        <div className="fixed top-4 right-4 z-50 px-4 py-3 bg-[#00E87A] text-black rounded-lg shadow-lg font-medium text-sm flex items-center gap-2 animate-in" data-testid="mail-scan-toast">
          <Check className="w-4 h-4" /> {toast}
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <p className="text-gray-400 text-sm">{scans.length} mail scan{scans.length !== 1 ? 's' : ''} uploaded</p>
        </div>
        <button
          onClick={() => { resetUploadForm(); setShowUpload(true); }}
          className="flex items-center gap-2 px-4 py-2 bg-[#00E87A] text-black font-medium rounded-lg hover:bg-[#00D46E] transition-colors text-sm"
          data-testid="upload-mail-scan-btn"
        >
          <Upload className="w-4 h-4" /> Upload Mail Scan
        </button>
      </div>

      {/* List */}
      {scans.length === 0 ? (
        <div className="bg-[#0D1117] border border-gray-700/50 rounded-xl p-12 text-center" data-testid="mail-scans-empty">
          <Mail className="w-12 h-12 text-gray-600 mx-auto mb-4" />
          <p className="text-gray-400 text-lg">No mail scans uploaded yet.</p>
        </div>
      ) : (
        <div className="bg-[#0D1117] border border-gray-700/50 rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="mail-scans-table">
              <thead>
                <tr className="border-b border-gray-700/50">
                  <th className="text-left px-4 py-3 text-xs font-medium text-gray-400 uppercase cursor-pointer select-none"
                      onClick={() => setSortDir(d => d === 'desc' ? 'asc' : 'desc')} data-testid="sort-date-header">
                    <span className="flex items-center gap-1">
                      Received {sortDir === 'desc' ? <ChevronDown className="w-3 h-3" /> : <ChevronUp className="w-3 h-3" />}
                    </span>
                  </th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-gray-400 uppercase">Sender</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-gray-400 uppercase">Urgency</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-gray-400 uppercase">Type</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-gray-400 uppercase hidden lg:table-cell">Uploaded By</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-gray-400 uppercase hidden md:table-cell">Uploaded</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-gray-400 uppercase hidden lg:table-cell">Notification</th>
                  <th className="text-right px-4 py-3 text-xs font-medium text-gray-400 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-700/30">
                {paginated.map((s) => (
                  <tr key={s.id} className="hover:bg-gray-800/30 transition-colors" data-testid={`scan-row-${s.id}`}>
                    <td className="px-4 py-3 text-white whitespace-nowrap">{formatDate(s.mail_received_date)}</td>
                    <td className="px-4 py-3 text-gray-300 max-w-[200px] truncate" title={s.mail_sender}>{s.mail_sender}</td>
                    <td className="px-4 py-3">
                      {s.mail_urgency === 'urgent' ? (
                        <span className="px-2 py-0.5 bg-amber-500/20 text-amber-400 text-xs font-medium rounded" data-testid={`urgency-badge-${s.id}`}>Urgent</span>
                      ) : (
                        <span className="px-2 py-0.5 bg-gray-600/30 text-gray-400 text-xs rounded">Routine</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-gray-400 text-xs">
                      {s.document_category === 'ra_mail_scan' ? 'RA' : 'MF'}
                    </td>
                    <td className="px-4 py-3 text-gray-400 text-xs hidden lg:table-cell">{s.uploaded_by_email?.split('@')[0] || '—'}</td>
                    <td className="px-4 py-3 text-gray-500 text-xs hidden md:table-cell" title={s.uploaded_at}>{formatDate(s.uploaded_at)}</td>
                    <td className="px-4 py-3 hidden lg:table-cell">
                      {s.mail_notified_at ? (
                        <span className="text-green-400 text-xs">Notified {formatDate(s.mail_notified_at)}</span>
                      ) : (
                        <button
                          onClick={() => handleNotify(s)}
                          disabled={notifyingId === s.id}
                          className="flex items-center gap-1 px-2 py-1 text-xs text-amber-400 bg-amber-500/10 rounded hover:bg-amber-500/20 transition-colors disabled:opacity-50"
                          data-testid={`notify-btn-${s.id}`}
                        >
                          {notifyingId === s.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Bell className="w-3 h-3" />}
                          Notify Now
                        </button>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button onClick={() => handleDownload(s)} disabled={downloadingId === s.id}
                          className="p-1.5 text-gray-400 hover:text-white rounded hover:bg-gray-700 transition-colors disabled:opacity-50"
                          title="Download" data-testid={`download-btn-${s.id}`}>
                          {downloadingId === s.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
                        </button>
                        <button onClick={() => openEdit(s)}
                          className="p-1.5 text-gray-400 hover:text-white rounded hover:bg-gray-700 transition-colors"
                          title="Edit" data-testid={`edit-btn-${s.id}`}>
                          <Pencil className="w-3.5 h-3.5" />
                        </button>
                        <button onClick={() => setShowDeleteConfirm(s)}
                          className="p-1.5 text-gray-400 hover:text-red-400 rounded hover:bg-red-500/10 transition-colors"
                          title="Delete" data-testid={`delete-btn-${s.id}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-gray-700/50">
              <span className="text-gray-500 text-xs">Page {page + 1} of {totalPages}</span>
              <div className="flex gap-2">
                <button onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0}
                  className="px-3 py-1 text-xs text-gray-400 bg-gray-800 rounded hover:bg-gray-700 disabled:opacity-30">Prev</button>
                <button onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))} disabled={page >= totalPages - 1}
                  className="px-3 py-1 text-xs text-gray-400 bg-gray-800 rounded hover:bg-gray-700 disabled:opacity-30">Next</button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Upload Drawer */}
      {showUpload && (
        <div className="fixed inset-0 bg-black/70 flex items-start justify-end z-50" onClick={(e) => { if (e.target === e.currentTarget) { setShowUpload(false); resetUploadForm(); } }}>
          <div className="h-full w-full max-w-lg bg-[#161B22] border-l border-gray-700 overflow-y-auto" data-testid="upload-drawer">
            <div className="sticky top-0 bg-[#161B22] border-b border-gray-700 px-6 py-4 flex items-center justify-between z-10">
              <h2 className="text-lg font-semibold text-white flex items-center gap-2"><Upload className="w-5 h-5 text-[#00E87A]" /> Upload Mail Scan</h2>
              <button onClick={() => { setShowUpload(false); resetUploadForm(); }} className="p-1 text-gray-400 hover:text-white" data-testid="upload-close-btn"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-6 space-y-5">
              {/* File */}
              <div>
                <label className="text-xs text-gray-400 uppercase tracking-wider block mb-1.5">File (PDF only, max 10MB) *</label>
                <input ref={fileRef} type="file" accept=".pdf" onChange={handleFileSelect} className="hidden" data-testid="upload-file-input" />
                <button onClick={() => fileRef.current?.click()}
                  className="w-full p-4 border border-dashed border-gray-600 rounded-lg text-gray-400 hover:border-[#00E87A] hover:text-[#00E87A] transition-colors text-sm"
                  data-testid="upload-file-btn">
                  {uploadFile ? (
                    <span className="flex items-center justify-center gap-2 text-white">
                      <FileText className="w-4 h-4 text-[#00E87A]" /> {uploadFile.name} ({formatBytes(uploadFile.size)})
                    </span>
                  ) : 'Click to select PDF file'}
                </button>
              </div>

              {/* Mail Type */}
              <div>
                <label className="text-xs text-gray-400 uppercase tracking-wider block mb-1.5">Mail Type *</label>
                <div className="flex gap-3">
                  <label className={`flex items-center gap-2 px-4 py-2.5 rounded-lg border cursor-pointer transition-colors text-sm flex-1 ${
                    mailType === 'ra_mail_scan' ? 'border-[#00E87A] bg-[#00E87A]/10 text-white' : hasRA ? 'border-gray-600 text-gray-400 hover:border-gray-500' : 'border-gray-700 text-gray-600 cursor-not-allowed opacity-50'
                  }`} title={!hasRA ? 'No active Registered Agent on this company' : ''}>
                    <input type="radio" name="mailType" value="ra_mail_scan" checked={mailType === 'ra_mail_scan'}
                      onChange={() => hasRA && setMailType('ra_mail_scan')} disabled={!hasRA}
                      className="sr-only" data-testid="radio-ra" />
                    <div className={`w-4 h-4 rounded-full border-2 flex-shrink-0 ${mailType === 'ra_mail_scan' ? 'border-[#00E87A] bg-[#00E87A]' : 'border-gray-500'}`}>
                      {mailType === 'ra_mail_scan' && <div className="w-full h-full flex items-center justify-center"><div className="w-1.5 h-1.5 bg-black rounded-full" /></div>}
                    </div>
                    RA Mail
                  </label>
                  <label className={`flex items-center gap-2 px-4 py-2.5 rounded-lg border cursor-pointer transition-colors text-sm flex-1 ${
                    mailType === 'mail_forward_scan' ? 'border-[#00E87A] bg-[#00E87A]/10 text-white' : hasMF ? 'border-gray-600 text-gray-400 hover:border-gray-500' : 'border-gray-700 text-gray-600 cursor-not-allowed opacity-50'
                  }`} title={!hasMF ? 'No active Mail Forwarding on this company' : ''}>
                    <input type="radio" name="mailType" value="mail_forward_scan" checked={mailType === 'mail_forward_scan'}
                      onChange={() => hasMF && setMailType('mail_forward_scan')} disabled={!hasMF}
                      className="sr-only" data-testid="radio-mf" />
                    <div className={`w-4 h-4 rounded-full border-2 flex-shrink-0 ${mailType === 'mail_forward_scan' ? 'border-[#00E87A] bg-[#00E87A]' : 'border-gray-500'}`}>
                      {mailType === 'mail_forward_scan' && <div className="w-full h-full flex items-center justify-center"><div className="w-1.5 h-1.5 bg-black rounded-full" /></div>}
                    </div>
                    Mail Forwarding
                  </label>
                </div>
              </div>

              {/* Sender */}
              <div>
                <label className="text-xs text-gray-400 uppercase tracking-wider block mb-1.5">Sender *</label>
                <input type="text" value={sender} onChange={(e) => setSender(e.target.value.slice(0, 200))}
                  placeholder="e.g. Georgia Secretary of State" maxLength={200}
                  className="w-full px-3 py-2.5 bg-gray-800 border border-gray-600 rounded-lg text-white text-sm focus:outline-none focus:border-[#00E87A] placeholder-gray-500"
                  data-testid="upload-sender-input" />
                <span className="text-gray-600 text-xs mt-0.5 block text-right">{sender.length}/200</span>
              </div>

              {/* Received Date */}
              <div>
                <label className="text-xs text-gray-400 uppercase tracking-wider block mb-1.5">Received Date *</label>
                <input type="date" value={receivedDate} onChange={(e) => setReceivedDate(e.target.value)}
                  max={new Date().toISOString().split('T')[0]}
                  className="w-full px-3 py-2.5 bg-gray-800 border border-gray-600 rounded-lg text-white text-sm focus:outline-none focus:border-[#00E87A] [color-scheme:dark]"
                  data-testid="upload-date-input" />
                {receivedDate && validateDate(receivedDate) && (
                  <p className="text-red-400 text-xs mt-1">{validateDate(receivedDate)}</p>
                )}
              </div>

              {/* Admin Note */}
              <div>
                <label className="text-xs text-gray-400 uppercase tracking-wider block mb-1.5">Admin Note (optional)</label>
                <textarea value={adminNote} onChange={(e) => setAdminNote(e.target.value.slice(0, 1000))}
                  placeholder="Internal note for this scan..." maxLength={1000} rows={3}
                  className="w-full px-3 py-2.5 bg-gray-800 border border-gray-600 rounded-lg text-white text-sm focus:outline-none focus:border-[#00E87A] placeholder-gray-500 resize-none"
                  data-testid="upload-note-input" />
                <span className="text-gray-600 text-xs mt-0.5 block text-right">{adminNote.length}/1000</span>
              </div>

              {/* Urgency */}
              <div>
                <label className="text-xs text-gray-400 uppercase tracking-wider block mb-1.5">Urgency *</label>
                <div className="flex gap-3">
                  <label className={`flex items-center gap-2 px-4 py-2.5 rounded-lg border cursor-pointer transition-colors text-sm flex-1 ${
                    urgency === 'urgent' ? 'border-amber-500 bg-amber-500/10 text-amber-400' : 'border-gray-600 text-gray-400 hover:border-gray-500'
                  }`}>
                    <input type="radio" name="urgency" value="urgent" checked={urgency === 'urgent'}
                      onChange={() => setUrgency('urgent')} className="sr-only" data-testid="radio-urgent" />
                    <div className={`w-4 h-4 rounded-full border-2 flex-shrink-0 ${urgency === 'urgent' ? 'border-amber-500 bg-amber-500' : 'border-gray-500'}`}>
                      {urgency === 'urgent' && <div className="w-full h-full flex items-center justify-center"><div className="w-1.5 h-1.5 bg-black rounded-full" /></div>}
                    </div>
                    Urgent
                  </label>
                  <label className={`flex items-center gap-2 px-4 py-2.5 rounded-lg border cursor-pointer transition-colors text-sm flex-1 ${
                    urgency === 'routine' ? 'border-[#00E87A] bg-[#00E87A]/10 text-white' : 'border-gray-600 text-gray-400 hover:border-gray-500'
                  }`}>
                    <input type="radio" name="urgency" value="routine" checked={urgency === 'routine'}
                      onChange={() => setUrgency('routine')} className="sr-only" data-testid="radio-routine" />
                    <div className={`w-4 h-4 rounded-full border-2 flex-shrink-0 ${urgency === 'routine' ? 'border-[#00E87A] bg-[#00E87A]' : 'border-gray-500'}`}>
                      {urgency === 'routine' && <div className="w-full h-full flex items-center justify-center"><div className="w-1.5 h-1.5 bg-black rounded-full" /></div>}
                    </div>
                    Routine
                  </label>
                </div>
              </div>

              {/* Notify checkbox */}
              <label className="flex items-center gap-3 cursor-pointer" data-testid="upload-notify-checkbox">
                <input type="checkbox" checked={notifyNow} onChange={(e) => setNotifyNow(e.target.checked)}
                  className="w-4 h-4 rounded border-gray-600 text-[#00E87A] focus:ring-[#00E87A] bg-gray-800" />
                <span className="text-gray-300 text-sm">Notify client by email now</span>
              </label>

              {/* Error */}
              {uploadErr && (
                <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 text-red-400 mt-0.5 flex-shrink-0" />
                  <p className="text-red-400 text-sm">{uploadErr}</p>
                </div>
              )}
            </div>
            {/* Buttons */}
            <div className="sticky bottom-0 bg-[#161B22] border-t border-gray-700 px-6 py-4 flex justify-end gap-3">
              <button onClick={() => { setShowUpload(false); resetUploadForm(); }}
                className="px-4 py-2 text-gray-400 hover:text-white transition-colors text-sm" data-testid="upload-cancel-btn">Cancel</button>
              <button onClick={handleUpload} disabled={!canSubmit || uploading}
                className="flex items-center gap-2 px-5 py-2 bg-[#00E87A] text-black font-medium rounded-lg hover:bg-[#00D46E] transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                data-testid="upload-submit-btn">
                {uploading ? <><Loader2 className="w-4 h-4 animate-spin" /> Uploading...</> : <><Upload className="w-4 h-4" /> Upload Scan</>}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Drawer */}
      {showEdit && (
        <div className="fixed inset-0 bg-black/70 flex items-start justify-end z-50" onClick={(e) => { if (e.target === e.currentTarget) setShowEdit(null); }}>
          <div className="h-full w-full max-w-lg bg-[#161B22] border-l border-gray-700 overflow-y-auto" data-testid="edit-drawer">
            <div className="sticky top-0 bg-[#161B22] border-b border-gray-700 px-6 py-4 flex items-center justify-between z-10">
              <h2 className="text-lg font-semibold text-white flex items-center gap-2"><Pencil className="w-5 h-5 text-[#00E87A]" /> Edit Mail Scan</h2>
              <button onClick={() => setShowEdit(null)} className="p-1 text-gray-400 hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-6 space-y-5">
              <div className="p-3 bg-gray-800/50 rounded-lg text-xs text-gray-500">
                <p>File: {showEdit.file_name}</p>
                <p>Category: {showEdit.document_category === 'ra_mail_scan' ? 'RA Mail' : 'Mail Forwarding'}</p>
                <p>Received: {showEdit.mail_received_date}</p>
              </div>
              <div>
                <label className="text-xs text-gray-400 uppercase tracking-wider block mb-1.5">Sender *</label>
                <input type="text" value={editSender} onChange={(e) => setEditSender(e.target.value.slice(0, 200))}
                  className="w-full px-3 py-2.5 bg-gray-800 border border-gray-600 rounded-lg text-white text-sm focus:outline-none focus:border-[#00E87A]"
                  data-testid="edit-sender-input" />
              </div>
              <div>
                <label className="text-xs text-gray-400 uppercase tracking-wider block mb-1.5">Admin Note</label>
                <textarea value={editNote} onChange={(e) => setEditNote(e.target.value.slice(0, 1000))}
                  rows={3} maxLength={1000}
                  className="w-full px-3 py-2.5 bg-gray-800 border border-gray-600 rounded-lg text-white text-sm focus:outline-none focus:border-[#00E87A] resize-none"
                  data-testid="edit-note-input" />
                <span className="text-gray-600 text-xs mt-0.5 block text-right">{editNote.length}/1000</span>
              </div>
              <div>
                <label className="text-xs text-gray-400 uppercase tracking-wider block mb-1.5">Urgency</label>
                <div className="flex gap-3">
                  {['urgent', 'routine'].map(u => (
                    <label key={u} className={`flex items-center gap-2 px-4 py-2.5 rounded-lg border cursor-pointer transition-colors text-sm flex-1 ${
                      editUrgency === u ? (u === 'urgent' ? 'border-amber-500 bg-amber-500/10 text-amber-400' : 'border-[#00E87A] bg-[#00E87A]/10 text-white') : 'border-gray-600 text-gray-400'
                    }`}>
                      <input type="radio" value={u} checked={editUrgency === u} onChange={() => setEditUrgency(u)} className="sr-only" />
                      <div className={`w-4 h-4 rounded-full border-2 flex-shrink-0 ${editUrgency === u ? (u === 'urgent' ? 'border-amber-500 bg-amber-500' : 'border-[#00E87A] bg-[#00E87A]') : 'border-gray-500'}`}>
                        {editUrgency === u && <div className="w-full h-full flex items-center justify-center"><div className="w-1.5 h-1.5 bg-black rounded-full" /></div>}
                      </div>
                      {u.charAt(0).toUpperCase() + u.slice(1)}
                    </label>
                  ))}
                </div>
              </div>
              {editErr && <p className="text-red-400 text-sm">{editErr}</p>}
            </div>
            <div className="sticky bottom-0 bg-[#161B22] border-t border-gray-700 px-6 py-4 flex justify-end gap-3">
              <button onClick={() => setShowEdit(null)} className="px-4 py-2 text-gray-400 hover:text-white text-sm" data-testid="edit-cancel-btn">Cancel</button>
              <button onClick={() => handleEdit(showEdit.id)} disabled={saving || !editSender.trim()}
                className="flex items-center gap-2 px-5 py-2 bg-[#00E87A] text-black font-medium rounded-lg hover:bg-[#00D46E] disabled:opacity-50 text-sm"
                data-testid="edit-save-btn">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Save Changes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
          <div className="bg-[#161B22] border border-gray-700 rounded-xl p-6 max-w-md w-full mx-4" data-testid="delete-scan-modal">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-red-500/20 flex items-center justify-center">
                <Trash2 className="w-5 h-5 text-red-400" />
              </div>
              <h3 className="text-lg font-semibold text-white">Delete Mail Scan</h3>
            </div>
            <p className="text-gray-400 mb-2">
              Soft delete this mail scan from <span className="text-white font-medium">{showDeleteConfirm.mail_sender}</span> (received {showDeleteConfirm.mail_received_date})?
            </p>
            <p className="text-gray-500 text-xs mb-6">It can be restored by engineering if needed.</p>
            <div className="flex justify-end gap-3">
              <button onClick={() => setShowDeleteConfirm(null)}
                className="px-4 py-2 text-gray-400 hover:text-white transition-colors text-sm" data-testid="delete-scan-cancel-btn">Cancel</button>
              <button onClick={() => handleDelete(showDeleteConfirm.id)} disabled={deletingId === showDeleteConfirm.id}
                className="flex items-center gap-2 px-4 py-2 bg-red-500 text-white font-medium rounded-lg hover:bg-red-600 disabled:opacity-50 text-sm"
                data-testid="delete-scan-confirm-btn">
                {deletingId === showDeleteConfirm.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MailScansSection;
